import axios from 'axios';
import { store } from '../store/store';
import { logout } from '../store/slices/authSlice';
import { clearAccessToken, getAccessToken } from './authStorage';
import { showGlobalToast } from './toastBus';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const buildHandledErrorResponse = (error) => ({
  data: error?.response?.data ?? null,
  status: error?.response?.status ?? 0,
  statusText: error?.response?.statusText ?? 'Échec de la requête',
  headers: error?.response?.headers ?? {},
  config: error?.config ?? {},
  request: error?.request ?? null,
  error,
  ok: false,
});

const extractMessage = (value) => {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && typeof value.message === 'string') return value.message;
  if (typeof value === 'object' && typeof value.code === 'string') return value.code;
  return null;
};

export const getErrorMessage = (error) => {
  // If there's a response from the server
  if (error.response) {
    const { data, status } = error.response;

    // Try to extract error message from various common backend error formats
    const msg = extractMessage(data?.error) ?? extractMessage(data);
    if (msg) return msg;

    // Fallback to default status messages
    switch (status) {
      case 400:
        return 'Données invalides';
      case 401:
        return 'Non autorisé. Veuillez vous reconnecter.';
      case 403:
        return "Vous n'avez pas les permissions pour effectuer cette action.";
      case 404:
        return 'Ressource introuvable.';
      case 422:
        return 'Erreur de validation. Veuillez vérifier votre saisie.';
      case 429:
        return 'Trop de requêtes. Veuillez réessayer plus tard.';
      default:
        return 'Une erreur est survenue. Veuillez réessayer plus tard.';
    }
  } else if (error.request) {
    return 'Aucune réponse du serveur. Vérifiez votre connexion réseau.';
  } else {
    return 'Une erreur est survenue. Veuillez réessayer plus tard.';
  }
};

api.interceptors.request.use(
  (config) => {
    try {
      const token = getAccessToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in request interceptor:', error);
      return config;
    }
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => ({ ...response, ok: true }),
  (error) => {
    const isCancelled = error?.code === 'ERR_CANCELED' || error?.message === 'canceled';
    if (!isCancelled && !error?.config?.skipErrorToast) {
      showGlobalToast(getErrorMessage(error), 'error');
    }

    const status = error.response?.status;

    if (status === 401) {
      clearAccessToken();
      store.dispatch(logout());
    }

    // Return a handled response object so callers don't need try/catch
    // for expected request failures.
    return buildHandledErrorResponse(error);
  }
);

export default api;
